import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

function UserProfileModal({ user }) {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    existingPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const profilePhotoUrl = user?.photoUrl || user?.profilePhoto || user?.imageUrl || "";

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "U";

  useEffect(() => {
    if (!showProfileModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showProfileModal]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    setPasswordError("");
    setPasswordMessage("");

    if (!passwordForm.existingPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("Please fill in all password fields.");
      return;
    }

    if (passwordForm.newPassword.length < 4) {
      setPasswordError("New password must be at least 4 characters.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirm password do not match.");
      return;
    }

    try {
      setPasswordLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch("/v1/clinic/users/password", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          existingPassword: passwordForm.existingPassword,
          newPassword: passwordForm.newPassword,
          confirmPassword: passwordForm.confirmPassword
        })
      });

      if (!res.ok) {
        const text = await res.text();
        setPasswordError(text || "Failed to change password.");
        return;
      }

      setPasswordMessage("Password updated successfully.");
      setPasswordForm({
        existingPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (err) {
      console.error(err);
      setPasswordError("Something went wrong while changing the password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) return null;

  const modal = (
    <>
      <div
        className="profile-modal-backdrop"
        onClick={() => setShowProfileModal(false)}
      />

      <div className="profile-modal-shell" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content profile-modal">
            <div className="modal-header profile-modal-header">
              <h5 className="modal-title">User Profile</h5>
              <button
                className="btn-close"
                onClick={() => setShowProfileModal(false)}
              ></button>
            </div>

            <div className="modal-body">
              <div className="profile-panel">
                <div className="profile-photo-frame">
                  {profilePhotoUrl ? (
                    <img
                      className="profile-photo"
                      src={profilePhotoUrl}
                      alt={user?.name || "User"}
                    />
                  ) : (
                    <div className="profile-photo profile-photo-fallback">
                      {getInitials(user?.name)}
                    </div>
                  )}
                </div>

                <div className="profile-details">
                  <h4 className="mb-1">{user?.name || "User"}</h4>
                  <p className="text-muted mb-3">{user?.username || ""}</p>

                  <div className="profile-meta-grid">
                    <div>
                      <span className="profile-meta-label">Role</span>
                      <span className="profile-meta-value">
                        {user?.role === 1 ? "Admin" : user?.role === 2 ? "Doctor" : "Staff"}
                      </span>
                    </div>

                    <div>
                      <span className="profile-meta-label">Clinic</span>
                      <span className="profile-meta-value">
                        {user?.clinicName || "N/A"}
                      </span>
                    </div>

                    <div>
                      <span className="profile-meta-label">Clinic ID</span>
                      <span className="profile-meta-value">
                        {user?.clinicId || "N/A"}
                      </span>
                    </div>

                    <div>
                      <span className="profile-meta-label">User ID</span>
                      <span className="profile-meta-value">
                        {user?.id || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-password-section mt-4">
                <button
                  type="button"
                  className="profile-password-toggle"
                  onClick={() => setShowPasswordSection((prev) => !prev)}
                >
                  <div>
                    <h5 className="mb-1">Change Password</h5>
                    <p className="mb-0">
                      Update your login password without leaving the profile screen.
                    </p>
                  </div>
                  <span className="profile-password-chevron">
                    {showPasswordSection ? "▲" : "▼"}
                  </span>
                </button>

                {showPasswordSection && (
                  <form className="profile-password-form mt-3" onSubmit={handlePasswordChange}>
                    <div className="row g-2">
                      <div className="col-12">
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Existing password"
                          value={passwordForm.existingPassword}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              existingPassword: e.target.value
                            }))
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <input
                          type="password"
                          className="form-control"
                          placeholder="New password"
                          value={passwordForm.newPassword}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              newPassword: e.target.value
                            }))
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Confirm new password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              confirmPassword: e.target.value
                            }))
                          }
                        />
                      </div>
                    </div>

                    {passwordError && (
                      <div className="alert alert-danger py-2 mt-3 mb-0">
                        {passwordError}
                      </div>
                    )}

                    {passwordMessage && (
                      <div className="alert alert-success py-2 mt-3 mb-0">
                        {passwordMessage}
                      </div>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setPasswordForm({
                            existingPassword: "",
                            newPassword: "",
                            confirmPassword: ""
                          })
                        }
                      >
                        Clear
                      </button>

                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? "Updating..." : "Update Password"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            <div className="modal-footer profile-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowProfileModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <button
        className="profile-chip"
        onClick={() => setShowProfileModal(true)}
        type="button"
      >
        <span className="profile-avatar">
          {profilePhotoUrl ? (
            <img src={profilePhotoUrl} alt={user?.name || "User"} />
          ) : (
            <span>{getInitials(user?.name)}</span>
          )}
        </span>

        <span className="profile-chip-text">
          <span className="profile-chip-name">{user?.name || "User"}</span>
          <span className="profile-chip-role">
            {user?.role === 1 ? "Admin" : user?.role === 2 ? "Doctor" : "Staff"}
          </span>
        </span>
      </button>

      {showProfileModal && createPortal(modal, document.body)}
    </>
  );
}

export default UserProfileModal;
